import { getApolloCache } from '@confluence/graphql';
import type { ReactionMutationUpdateFn } from '@confluence/reactions';

import { ReactionsCommentsFragment } from './ReactionsCommentsFragment';
import type { ReactionsCommentsFragment as FragmentType } from './__types__/ReactionsCommentsFragment';

export const updateCacheForReactions: ReactionMutationUpdateFn = (
	emojiId,
	actionType,
	commentId,
	pageId,
) => {
	const cache = getApolloCache();
	const id = `$Comment:${commentId}.reactionsSummary({"childType":"comment","contentType":"page","pageId":"${pageId}"})`;

	const response = cache.readFragment<FragmentType>({
		id,
		fragment: ReactionsCommentsFragment,
	});

	if (!response) {
		return;
	}

	const reactionsAri = response?.ari;
	const reactionsContainerAri = response?.containerAri;
	const reactionsNodes = response?.reactionsSummaryForEmoji || [];
	let reactionsCount = response?.reactionsCount || 0;

	const updatedReactionsNodes = [...reactionsNodes];

	const updatedFragment = { ...response };

	if (actionType === 'add') {
		const reaction = updatedReactionsNodes.find((item) => item?.emojiId === emojiId);

		// If reaction exists, update its attributes
		if (reaction && !reaction.reacted) {
			reaction.count++;
			reaction.reacted = true;
		} else {
			// Otherwise, create a new reaction
			const newReaction = {
				emojiId,
				count: 1,
				reacted: true,
				id: `${reactionsContainerAri}|${reactionsAri}|${emojiId}`,
				__typename: 'ReactionsSummaryForEmoji',
			};

			updatedReactionsNodes.push(newReaction);
		}

		++reactionsCount;
	} else if (actionType === 'delete') {
		const reactionIdx = updatedReactionsNodes.findIndex((item) => item?.emojiId === emojiId);

		// If reaction exists, update its attributes
		if (reactionIdx !== -1) {
			const reaction = updatedReactionsNodes[reactionIdx];

			// If we have more than one reaction, remove our user from it and decrement the count
			if (reaction && reaction.count > 1) {
				reaction.count--;
				reaction.reacted = false;
			} else {
				// Otherwise, remove the reaction entirely
				updatedReactionsNodes.splice(reactionIdx, 1);
			}
		}

		--reactionsCount;
	}

	updatedFragment.reactionsSummaryForEmoji = updatedReactionsNodes;
	updatedFragment.reactionsCount = reactionsCount;

	cache.writeFragment({
		id,
		fragment: ReactionsCommentsFragment,
		data: {
			...updatedFragment,
		},
	});
};
